import { useEffect, useMemo, useState } from "react";
import { api, currency, qty } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "@/components/ui/select";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell
} from "@/components/ui/table";
import { Plus, Pencil, Sliders, Search, Calculator } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PriceListDownload from "@/components/PriceListDownload";
import StockListDownload from "@/components/StockListDownload";

const emptyProduct = {
  sku: "", name: "", category: "", unit: "unit", selling_mode: "Per Unit",
  pack_size: 1, quantity_on_hand: 0, reorder_level: 0,
  unit_price: 0, unit_cost: 0, desired_margin: 0.4, risk_buffer: 0.05,
  expected_monthly_units: 25, special_deal: "", notes: "",
};

function statusOf(p) {
  if (p.pending_count) return "pending";
  if (p.reorder_level > 0 && p.quantity_on_hand <= p.reorder_level) return "low";
  if (p.reorder_level > 0 && p.quantity_on_hand <= p.reorder_level * 2) return "medium";
  return "good";
}

const statusBadge = {
  low: "bg-red-100 text-red-800 hover:bg-red-100",
  medium: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  good: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  pending: "bg-amber-100 text-amber-800 hover:bg-amber-100",
};

const statusLabel = {
  low: "low",
  medium: "medium",
  good: "good",
  pending: "Count pending",
};

export default function Inventory() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [meta, setMeta] = useState({ categories: [], units: [], selling_modes: [] });
  const [editing, setEditing] = useState(null); // product or "new"
  const [adjusting, setAdjusting] = useState(null); // product
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustReason, setAdjustReason] = useState("manual adjustment");

  const load = () => {
    api.get("/products", { params: { q: q || undefined, category: category === "All" ? undefined : category } })
      .then(r => setProducts(r.data));
  };

  useEffect(() => { load(); }, [q, category]);
  useEffect(() => { api.get("/products/meta").then(r => setMeta(r.data)); }, []);

  const filtered = useMemo(() => products.filter(p => p.active), [products]);

  const startEdit = (p) => setEditing({ ...p });
  const startNew = () => setEditing({ ...emptyProduct, category: meta.categories[0] || "" });

  const saveProduct = async () => {
    try {
      if (editing.id) {
        const { id, created_at, updated_at, active, quantity_on_hand, ...body } = editing;
        await api.patch(`/products/${id}`, body);
        toast.success("Product updated");
      } else {
        await api.post("/products", editing);
        toast.success("Product created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const submitAdjust = async () => {
    try {
      await api.post(`/products/${adjusting.id}/adjust`, {
        delta: Number(adjustDelta),
        reason: adjustReason,
      });
      toast.success("Stock adjusted");
      setAdjusting(null); setAdjustDelta(""); setAdjustReason("manual adjustment");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Adjustment failed");
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2">Inventory</div>
          <h1 className="font-display text-4xl tracking-tight" data-testid="inventory-title">Product Catalog</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {["platform_owner", "vendor_owner", "admin", "manager"].includes(user?.role) && <StockListDownload />}
          {["platform_owner", "vendor_owner", "admin", "manager"].includes(user?.role) && <PriceListDownload />}
          {canEdit && (
            <Button data-testid="new-product-btn" onClick={startNew} className="rounded-full bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New Product
            </Button>
          )}
        </div>
      </div>

      <Card className="p-4 mb-4 border-border shadow-sm">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              data-testid="inventory-search"
              placeholder="Search by name or SKU…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="md:w-64" data-testid="category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Categories</SelectItem>
              {meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <Table>
            <TableHeader className="bg-muted/40 sticky top-0">
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Reorder</TableHead>
                <TableHead>Status</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(p => {
                const s = statusOf(p);
                return (
                  <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`row-${p.sku}`}>
                    <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                    <TableCell>
                      <div className="font-medium">{p.name}</div>
                      {p.special_deal && (
                        <div className="text-[11px] text-secondary">{p.special_deal}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.category}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{currency(p.unit_cost)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{currency(p.unit_price)}</TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums">{qty(p.quantity_on_hand)} {p.unit}</TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums">{qty(p.reorder_level)}</TableCell>
                    <TableCell>
                      <Badge className={statusBadge[s]}>{statusLabel[s]}</Badge>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setAdjusting(p)} data-testid={`adjust-${p.sku}`}>
                          <Sliders className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => startEdit(p)} data-testid={`edit-${p.sku}`}>
                          <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 9 : 8} className="text-center py-10 text-muted-foreground">
                    No products yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Product editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] md:max-h-none overflow-y-auto md:overflow-visible">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing?.id ? "Edit product" : "New product"}
            </DialogTitle>
            <DialogDescription>
              Prices are in ZAR. Selling price can be calculated via the Pricing Engine.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="SKU">
                <Input data-testid="edit-sku" value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} />
              </Field>
              <Field label="Name">
                <Input data-testid="edit-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </Field>
              <Field label="Category">
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="edit-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Unit">
                <Select value={editing.unit} onValueChange={(v) => setEditing({ ...editing, unit: v })}>
                  <SelectTrigger data-testid="edit-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.units.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Selling Mode">
                <Select value={editing.selling_mode} onValueChange={(v) => setEditing({ ...editing, selling_mode: v })}>
                  <SelectTrigger data-testid="edit-sellmode"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.selling_modes.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Cost price (R)">
                <Input type="number" step="0.01" value={editing.unit_cost} onChange={(e) => setEditing({ ...editing, unit_cost: Number(e.target.value) })} />
              </Field>
              <Field label="Selling price (R)">
                <Input type="number" step="0.01" data-testid="edit-price" value={editing.unit_price} onChange={(e) => setEditing({ ...editing, unit_price: Number(e.target.value) })} />
              </Field>
              <Field label="Proposed margin (0-1)">
                <Input type="number" step="0.01" value={editing.desired_margin} onChange={(e) => setEditing({ ...editing, desired_margin: Number(e.target.value) })} />
              </Field>
              <Field label="Risk buffer (0-1)">
                <Input type="number" step="0.01" value={editing.risk_buffer} onChange={(e) => setEditing({ ...editing, risk_buffer: Number(e.target.value) })} />
              </Field>
              <div className="md:col-span-2 flex items-center justify-between rounded-md border border-dashed p-3 bg-muted/40">
                <div className="text-xs text-muted-foreground">
                  Push the pricing engine&apos;s suggested price into <span className="font-medium text-foreground">Selling price</span> above.
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="recalc-price-btn"
                  onClick={async () => {
                    try {
                      const r = await api.post("/pricing/calculate", {
                        unit_cost: editing.unit_cost,
                        desired_margin: editing.desired_margin,
                        risk_buffer: editing.risk_buffer,
                        expected_monthly_units: editing.expected_monthly_units,
                      });
                      setEditing({ ...editing, unit_price: r.data.suggested_price });
                      toast.success(`Suggested ${r.data.suggested_price} — applied`);
                    } catch (e) { toast.error("Calculation failed"); }
                  }}
                >
                  <Calculator className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Calculate from engine
                </Button>
              </div>
              {!editing.id && (
                <Field label="Opening qty">
                  <Input type="number" step="0.01" data-testid="edit-qty" value={editing.quantity_on_hand} onChange={(e) => setEditing({ ...editing, quantity_on_hand: Number(e.target.value) })} />
                </Field>
              )}
              <Field label="Reorder level">
                <Input type="number" step="0.01" data-testid="edit-reorder" value={editing.reorder_level} onChange={(e) => setEditing({ ...editing, reorder_level: Number(e.target.value) })} />
              </Field>
              <Field label="Expected monthly units">
                <Input type="number" step="1" value={editing.expected_monthly_units} onChange={(e) => setEditing({ ...editing, expected_monthly_units: Number(e.target.value) })} />
              </Field>
              <Field label="Special deal (e.g. '3 for R150')">
                <Input value={editing.special_deal} onChange={(e) => setEditing({ ...editing, special_deal: e.target.value })} />
              </Field>
              <div className="md:col-span-2">
                <Field label="Notes">
                  <Textarea rows={2} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
                </Field>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="save-product-btn" onClick={saveProduct} className="rounded-full bg-primary hover:bg-primary/90">Save product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock adjust */}
      <Dialog open={!!adjusting} onOpenChange={(o) => !o && setAdjusting(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Adjust stock</DialogTitle>
            <DialogDescription>{adjusting?.name} · current {adjusting?.quantity_on_hand} {adjusting?.unit}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Delta (positive to add, negative to remove)">
              <Input data-testid="adjust-delta" type="number" step="0.01" value={adjustDelta} onChange={(e) => setAdjustDelta(e.target.value)} />
            </Field>
            <Field label="Reason">
              <Input data-testid="adjust-reason" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdjusting(null)}>Cancel</Button>
            <Button data-testid="submit-adjust-btn" onClick={submitAdjust} className="rounded-full bg-primary hover:bg-primary/90">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
